/**
 * custom-price-match.js
 * Shared CCC / Phaser feature (phaser: shared) — price-match request dialog
 * for blocks/custom-price-match.liquid.
 *
 * "Request a match" opens a theme-native <dialog class="dialog-modal"> holding
 * a Shopify {% form 'contact' %}: competitor link + price, contact details,
 * Variant dropdown, hidden Product / Our price / Request fields. The request arrives through
 * Shopify's built-in contact email — no app, nothing custom server-side.
 *
 * Dialog conventions copied from custom-demo-booking.js / the shipping
 * estimator (the theme's proven path): reparent the <dialog> onto
 * document.body before showModal(), lockScroll/unlockScroll, close via the
 * block's own transform keyframes raced with a timeout, light-dismiss on
 * backdrop, sending state on submit, success/error panel auto-reopen after
 * the POST redirect.
 */

import { lockScroll, unlockScroll, onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

class PriceMatch extends HTMLElement {
  connectedCallback() {
    this._dialog = this.querySelector('[data-cpm-dialog]');
    this._openBtn = this.querySelector('[data-cpm-open]');
    if (!this._dialog || !this._openBtn) return;

    this._form = this._dialog.querySelector('form');
    this._url = this._dialog.querySelector('[data-cpm-url]');
    this._price = this._dialog.querySelector('[data-cpm-price]');
    this._hint = this._dialog.querySelector('[data-cpm-hint]');
    this._variantSelect = this._dialog.querySelector('[data-cpm-variant]');
    this._priceLabel = this._dialog.querySelector('[data-cpm-our-price-label]');
    this._priceField = this._dialog.querySelector('[data-cpm-our-price]');
    this._productField = this._dialog.querySelector('[data-cpm-product]');

    this._openBtn.addEventListener('click', (event) => {
      event.preventDefault();
      this._open();
    });
    this._dialog.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.target.closest('[data-cpm-close]')) {
        this._close();
        return;
      }
      if (event.target === this._dialog) this._close();
    });
    this._dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this._close();
    });
    this._form?.addEventListener('submit', (event) => this._guardSubmit(event));
    this._variantSelect?.addEventListener('change', () => this._applySelectedVariant());
    document.addEventListener(ThemeEvents.variantUpdate, this._onVariantUpdate);

    // Server-rendered success or error state (present only when this form's
    // contact[id] matched the POST): reopen so the customer sees the outcome.
    if (this._dialog.querySelector('.cpm-outcome')) {
      this._open();
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdate, this._onVariantUpdate);
    if (this._dialog?.open) {
      unlockScroll(this._dialog);
      this._dialog.close();
    }
    if (this._dialogHosted && this._dialogHome?.parent) {
      this._dialogHome.parent.insertBefore(this._dialog, this._dialogHome.next);
      this._dialogHosted = false;
    }
  }

  /**
   * Horizon only morphs the variant picker on a variant change, so the dropdown
   * follows the page here. Quick add changes for other products match no option
   * and are ignored.
   * @param {Event} event
   */
  _onVariantUpdate = (event) => {
    const variantId = String(/** @type {CustomEvent} */ (event).detail?.resource?.id ?? '');
    const option = [...(this._variantSelect?.options ?? [])].find((item) => item.dataset.variantId === variantId);
    if (!option) return;

    this._variantSelect.value = option.value;
    this._applySelectedVariant();
  };

  /** Points Our price and the Product link at the variant in the dropdown. */
  _applySelectedVariant() {
    const option = this._variantSelect?.selectedOptions[0];
    if (!option) return;

    const { price = '', variantId = '' } = option.dataset;
    if (this._priceLabel) this._priceLabel.textContent = price;
    if (this._priceField) this._priceField.value = price;
    if (this._productField) {
      this._productField.value = this._productField.value.replace(/\?variant=\d+$/, `?variant=${variantId}`);
    }
  }

  _guardSubmit(event) {
    // Light validation with friendly hints; the browser handles required/url.
    const url = (this._url?.value || '').trim();
    if (url && !/^https?:\/\/.+\..+/.test(url)) {
      event.preventDefault();
      this._showHint('That link doesn’t look right — paste the full competitor page URL (starting with http).');
      this._url?.focus();
      return;
    }
    const price = parseFloat(String(this._price?.value || '').replace(/[^0-9.]/g, ''));
    if (this._price && (!Number.isFinite(price) || price <= 0)) {
      event.preventDefault();
      this._showHint('Enter the competitor’s price as a number, e.g. 2299.00.');
      this._price?.focus();
      return;
    }
    // Normalise the price so the email reads cleanly.
    if (this._price) this._price.value = price.toFixed(2);
    if (this._submitting) {
      event.preventDefault();
      return;
    }
    this._submitting = true;
    this._dropBlankOptionalFields();
    const btn = this._form?.querySelector('.cpm-submit');
    requestAnimationFrame(() => {
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'Sending your request…';
      }
    });
  }

  /**
   * Shopify's contact notification prints one "Field: value" row per submitted
   * `contact[*]` key, so leaving "Anything else?" blank mails us an empty
   * "Body:" row. An unnamed control is not submitted at all, so dropping the
   * name attribute drops the row — the request email only lists what the
   * customer actually filled in.
   *
   * Required fields and hidden context (Product / Our price / Request / id) are
   * never touched. Names are restored on the next tick so a submit that somehow
   * does not navigate can still be retried with the fields intact; the form
   * data is serialised before that callback runs.
   */
  _dropBlankOptionalFields() {
    const fields = this._form?.querySelectorAll('input[name^="contact["], textarea[name^="contact["]');
    if (!fields?.length) return;

    /** @type {Array<[Element, string]>} */
    const dropped = [];

    for (const field of fields) {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) continue;
      if (field.type === 'hidden' || field.required) continue;
      if (field.value.trim() !== '') continue;

      dropped.push([field, field.name]);
      field.removeAttribute('name');
    }

    if (!dropped.length) return;

    setTimeout(() => {
      for (const [field, name] of dropped) field.setAttribute('name', name);
    }, 0);
  }

  _showHint(message) {
    if (!this._hint) return;
    this._hint.textContent = message;
    this._hint.hidden = !message;
  }

  /** Reparent onto document.body so top-layer behaviour can't be broken by
   *  an ancestor's display (same rule as the other custom dialogs). */
  _hostDialogOnBody() {
    if (this._dialogHosted || this._dialog.parentElement === document.body) return;
    this._dialogHome = {
      parent: this._dialog.parentNode,
      next: this._dialog.nextSibling,
    };
    document.body.appendChild(this._dialog);
    this._dialogHosted = true;
  }

  _open() {
    if (this._dialog.open) return;
    this._hostDialogOnBody();
    lockScroll(this._dialog);
    this._dialog.showModal();
  }

  async _close() {
    const dialog = this._dialog;
    if (!dialog?.open) return;
    dialog.classList.add('dialog-closing');
    await Promise.race([onAnimationEnd(dialog, undefined, { subtree: false }), wait(400)]);
    dialog.classList.remove('dialog-closing');
    unlockScroll(dialog);
    dialog.close();
  }
}

if (!customElements.get('ccc-price-match')) {
  customElements.define('ccc-price-match', PriceMatch);
}
